import { useState, useEffect, useRef, useLayoutEffect } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Nav from "react-bootstrap/Nav";
import Tab from "react-bootstrap/Tab";
import { FaHome, FaBuilding, FaMapMarkerAlt, FaCheck, FaShieldAlt, FaChartLine } from "react-icons/fa";

const ORANGE = "#e8973a";
const DARK_NAVY = "#14213d";

const TAB_DATA = [
  {
    key: "trust",
    icon: FaHome,
    tabTitle: "Quality & Trust",
    heading: "Building Trust Through Quality Real Estate",
    paragraphs: [
      "Siddhivinayak Group is a trusted real estate developer in Nagpur committed to creating exceptional residential communities, premium apartments, luxury flats, and investment-ready RL plots. With a strong reputation for transparency, construction excellence, and customer satisfaction, we have become one of the preferred choices for homebuyers and property investors seeking long-term value.",
      "As Nagpur continues its transformation into one of India's fastest-growing smart cities, the demand for quality housing and strategically located investment opportunities is increasing rapidly. We meet this demand by developing thoughtfully planned residential projects that combine modern architecture, superior amenities, and excellent connectivity.",
    ],
  },
  {
    key: "developer",
    icon: FaBuilding,
    tabTitle: "Leading Developer",
    heading: "Leading Real Estate Developer",
    paragraphs: [
      "Over the years, Siddhivinayak Group has established itself as a respected name in Nagpur's real estate market. Our developments focus on:",
    ],
    pills: [
      "Prime Locations",
      "Modern Infrastructure",
      "Sustainable Development",
      "Smart Living Spaces",
      "Enhanced Security",
      "Future Investment Growth",
    ],
  },
  {
    key: "locations",
    icon: FaMapMarkerAlt,
    tabTitle: "Prime Locations",
    heading: "Prime Locations & Investment Opportunities",
    paragraphs: [
      "Location plays a crucial role in property appreciation and lifestyle quality. Our projects are strategically located in high-growth areas including Ram Nagar, Hanuman Nagar, Narsala, Bahadura, and Ambazari Vicinity.",
      "For investors seeking flexibility and long-term growth, our RL plots in Nagpur provide an excellent opportunity. Siddhivinayak Nagari offers carefully planned residential plots equipped with cement concrete roads, underground utilities, and secure community layouts.",
    ],
  },
];

const TRANSPARENCY_POINTS = [
  "Ethical Business Practices",
  "Transparent Transactions",
  "Timely Communication",
  "Quality Assurance",
];

const WHY_NAGPUR_POINTS = [
  "MIHAN Development",
  "Metro Connectivity",
  "Smart City Projects",
  "Industrial & Logistics Hub",
];

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, inView];
}

function Reveal({ children, delay = 0 }) {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(24px)",
        transition: `opacity 0.6s ease ${delay}ms, transform 0.6s ease ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

// Shared renderer so the visible tab and the hidden measurement clone
// always render identical markup (needed for accurate height measurement).
function TabBody({ tab }) {
  return (
    <>
      <h3 style={{ fontWeight: 600, marginBottom: "12px" }}>{tab.heading}</h3>
      <TabBodyContent tab={tab} />
    </>
  );
}

// Height-measurement-only renderer: identical spacing/typography as
// TabBody but WITHOUT the <h3>. Used for the off-screen clones so the
// DOM has only one tab's heading visible to crawlers/screen-readers
// while we still measure the tallest body for contentHeight.
function TabBodyContent({ tab }) {
  return (
    <>
      {tab.paragraphs.map((p, i) => (
        <p
          key={i}
          style={{
            color: "#6c7280",
            marginBottom: "10px",
            textAlign: "justify",
          }}
        >
          {p}
        </p>
      ))}
      {tab.pills && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "10px",
            marginTop: "14px",
          }}
        >
          {tab.pills.map((pill) => (
            <span key={pill} className="po-pill-outline">
              <FaCheck style={{ color: ORANGE }} />
              {pill}
            </span>
          ))}
        </div>
      )}
    </>
  );
}

const TAB_KEYS = TAB_DATA.map((tab) => tab.key);
const AUTO_SWITCH_INTERVAL = 3000; // ms between automatic tab switches

function ProjectsOverviewSection({ sectionSpace }) {
  const [activeKey, setActiveKey] = useState(TAB_KEYS[0]);
  const timerRef = useRef(null);

  // ── Equal-height fix ──
  // Bootstrap's own `.tab-pane { display: none }` rule fights any CSS
  // trick to keep inactive panes "in flow" for sizing purposes. Instead,
  // we measure each tab's real rendered height once (off-screen, via
  // hidden clones using the same TabBody markup), take the tallest, and
  // lock the visible Tab.Content container to that height. Switching
  // tabs then never changes the card's size, however different their
  // actual content length is.
  const [contentHeight, setContentHeight] = useState(null);
  const measureRefs = useRef([]);

  useLayoutEffect(() => {
    const heights = measureRefs.current.map((el) => (el ? el.offsetHeight : 0));
    const max = Math.max(...heights, 0);
    if (max > 0) setContentHeight(max);
  }, []);

  // Starts (or restarts) the auto-advance timer from whatever tab is
  // currently active. Called on mount and again after any manual click,
  // so auto-switching resumes smoothly from the user's choice instead
  // of fighting it or jumping unexpectedly.
  const startTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setActiveKey((current) => {
        const idx = TAB_KEYS.indexOf(current);
        return TAB_KEYS[(idx + 1) % TAB_KEYS.length];
      });
    }, AUTO_SWITCH_INTERVAL);
  };

  useEffect(() => {
    startTimer();
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = (key) => {
    setActiveKey(key);
    startTimer(); // reset the countdown so it doesn't jump right after a manual click
  };

  return (
    <div className={`ltn__projects-overview-area po-theme ${sectionSpace}`}>
      <Container>
        <Reveal>
          <h2 className="section-title text-center mb-15" style={{ fontWeight: 600 }}>
            Premium Residential Projects &amp; RL Plots in Nagpur
          </h2>
          <p
            className="text-center mb-30"
            style={{
              color: "#6c7280",
              width: "100%",
              margin: "0 auto 30px",
              textAlign: "justify",
            }}
          >
            A trusted name in Nagpur's real estate market — built on quality,
            transparency, and long-term investment value.
          </p>
        </Reveal>

        <Row className="align-items-stretch">
          {/* Left: Tabbed content */}
          <Col xs={12} lg={8} className="mb-30 mb-lg-0">
            <Reveal delay={100}>
              <div
                style={{
                  background: "#fff",
                  borderRadius: "12px",
                  padding: "24px",
                  boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
                  height: "100%",
                }}
              >
                <div style={{ position: "relative" }}>
                  {/* Off-screen clones of every tab's body, used only to
                      measure the tallest one on mount. Not visible, not
                      interactive, removed from normal layout flow. Uses
                      TabBodyContent (no <h3>) so the DOM has only one
                      tab heading visible to crawlers. */}
                  <div
                    aria-hidden="true"
                    inert=""
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      visibility: "hidden",
                      pointerEvents: "none",
                      zIndex: -1,
                    }}
                  >
                    {TAB_DATA.map((tab, i) => (
                      <div key={tab.key} ref={(el) => (measureRefs.current[i] = el)}>
                        <TabBodyContent tab={tab} />
                      </div>
                    ))}
                  </div>

                  <Tab.Container
                    activeKey={activeKey}
                    onSelect={handleSelect}
                    unmountOnExit
                    mountOnEnter
                  >
                    <Nav className="po-tabs mb-25">
                      {TAB_DATA.map((tab) => {
                        const TabIcon = tab.icon;
                        return (
                          <Nav.Item key={tab.key}>
                            <Nav.Link eventKey={tab.key} className="po-tab-link">
                              <TabIcon style={{ marginRight: "6px" }} />
                              {tab.tabTitle}
                            </Nav.Link>
                          </Nav.Item>
                        );
                      })}
                    </Nav>

                    <Tab.Content
                      style={contentHeight ? { minHeight: contentHeight } : undefined}
                    >
                      {TAB_DATA.map((tab) => (
                        <Tab.Pane eventKey={tab.key} key={tab.key}>
                          <TabBody tab={tab} />
                        </Tab.Pane>
                      ))}
                    </Tab.Content>
                  </Tab.Container>
                </div>
              </div>
            </Reveal>
          </Col>

          {/* Right: Compact highlight cards */}
          <Col xs={12} lg={4} className="d-flex flex-column">
            <Reveal delay={200}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  gap: "20px",
                }}
              >
                <div
                  style={{
                    background: DARK_NAVY,
                    borderRadius: "12px",
                    padding: "20px",
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <h3
                    style={{
                      fontWeight: 600,
                      marginBottom: "12px",
                      color: "#ffffff",
                      fontSize: "1.05rem",
                    }}
                  >
                    <FaShieldAlt style={{ marginRight: "8px" }} />
                    Commitment to Transparency
                  </h3>
                  <p
                    style={{
                      color: "rgba(255, 255, 255, 0.75)",
                      fontSize: "13.5px",
                      lineHeight: 1.6,
                      marginBottom: "14px",
                    }}
                  >
                    Trust is the foundation of every successful real estate relationship. Our goal is to build lasting relationships with homeowners and investors.
                  </p>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                    {TRANSPARENCY_POINTS.map((point) => (
                      <span key={point} className="po-pill-dark">
                        {point}
                      </span>
                    ))}
                  </div>
                </div>

                <div
                  style={{
                    border: `1.5px solid ${ORANGE}`,
                    borderRadius: "12px",
                    padding: "20px",
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <h3
                    style={{
                      fontWeight: 600,
                      marginBottom: "12px",
                      color: "#1a1a1a",
                      fontSize: "1.05rem",
                    }}
                  >
                    <FaChartLine style={{ marginRight: "8px", color: ORANGE }} />
                    Why Nagpur?
                  </h3>

                  <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                    {WHY_NAGPUR_POINTS.map((point) => (
                      <span key={point} className="po-pill-light">
                        {point}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          </Col>
        </Row>
      </Container>

    </div>
  );
}

export default ProjectsOverviewSection;